// cspell:disable
import { Currency } from "@redotech/money/currencies";
import { CanadaPostManifestMode } from "@redotech/redo-fulfillment-carrier-account-common/canada-post";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import {
  FreightProvider,
  FreightServiceLevel,
  KnownSCAC,
} from "@redotech/redo-fulfillment-carrier-common/freight";
import { FulfillmentOrderAddressSchema } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { freightBookingIdFormat } from "@redotech/redo-fulfillment-id";
import { surchargeSchema_ } from "@redotech/redo-fulfillment-outbound-labels-common/surcharge";
import { OutboundParcelSchema } from "@redotech/redo-fulfillment-parcel-common/parcel";
import { RateSchema } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { Provider } from "@redotech/redo-order-common/provider";
import { TrackingPlatform } from "@redotech/redo-order-tracking-tracker-common/tracking-platform";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { RedoShopifyFulfillmentDisplayStatus } from "./fulfillment-display-status";
import { FulfillmentGroup } from "./fulfillment-group";
import {
  FulfillmentLineItemSchema,
  IFulfillmentLineItem,
} from "./fulfillment-line-item";
import { RedoShopifyFulfillmentStatus } from "./fulfillment-status";
import { BatchState, ScanFormStatus } from "./scan-form";

export enum OmsInsuranceProvider {
  EASYPOST = "easypost",
  SHIPSURANCE = "shipsurance",
}

export const RecordedShipmentSchema = z.object({
  shipmentDate: z.union([z.date(), z.string()]).nullish(),
  insuranceProvider: createEnum(OmsInsuranceProvider, {
    enumName: "OmsInsuranceProvider",
  }),
  shipmentId: z.string(),
  trackingNumber: z.string(),
  recordSourceIdentifier: z.string().nullish(),
  declaredValue: z.number().nullish(),
});
export type RecordedShipment = z.infer<typeof RecordedShipmentSchema>;

export const ShipsuranceClaimSchema = z.object({
  claimId: z.string(),
  claimRetrievalCode: z.string(),
  shipmentId: z.string(),
});
export type ShipsuranceClaim = z.infer<typeof ShipsuranceClaimSchema>;
export interface IShipsuranceClaim extends z.ZodType<ShipsuranceClaim> {}

export const RedoFulfillmentSchema = z.object({
  provider: createEnum(Provider, { enumName: "Provider" }),
  externalId: z.string(),
  name: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
  estimatedDeliveryAt: z.date().nullish(),
  originAddress: FulfillmentOrderAddressSchema,
  destinationAddress: FulfillmentOrderAddressSchema,
  inTransitAt: z.date().nullish(),
  deliveredAt: z.date().nullish(),
  displayStatus: createEnum(RedoShopifyFulfillmentDisplayStatus, {
    enumName: "RedoShopifyFulfillmentDisplayStatus",
  }).nullish(),
  status: createEnum(RedoShopifyFulfillmentStatus, {
    enumName: "RedoShopifyFulfillmentStatus",
  }),
  requiresShipping: z.boolean(),
  totalQuantity: z.number(),
  lineItems: z.array(FulfillmentLineItemSchema as IFulfillmentLineItem),
  trackingInfo: z.array(
    z.object({
      company: z.string().nullish(),
      url: z.string().nullish(),
      number: z.string().nullish(),
    }),
  ),
  order: z
    .object({
      id: z.string(),
      name: z.string(),
      tags: z.array(z.string()).nullish(),
      note: z.string(),
      createdAt: z.date(),
    })
    .nullish(),
  customer: z
    .object({
      id: z.string(),
      name: z.string(),
      tags: z.array(z.string()).nullish(),
      numOrders: z.number().nullish(),
      email: z.string().nullish(),
      phone: z.string().nullish(),
      note: z.string().nullish(),
      localizations: z.array(z.string()).nullish(),
    })
    .nullish(),
  deleted: z.boolean().nullish(),
  raw: z.any().nullish(),
});
export type RedoFulfillment = z.infer<typeof RedoFulfillmentSchema>;
export interface IRedoFulfillment extends z.ZodType<RedoFulfillment> {}

export const OutboundShipmentLabelSchema = z.object({
  url: z.string(),
  printed: z.date().optional(),
  commercialInvoiceUrl: z.string().optional().nullish(),
  comInvoiceSubmittedElectronically: z.boolean().nullish(),
  labelDate: z.date().nullish(),
});
export type OutboundShipmentLabel = z.infer<typeof OutboundShipmentLabelSchema>;

export const OutboundShipmentLineItemSchema = z.object({
  id: z.string(),
  quantity: z.number(),
  sku: z.string().nullish(),
});
export type OutboundShipmentLineItem = z.infer<
  typeof OutboundShipmentLineItemSchema
>;

export const LineItemsByFulfillmentOrderSchema = z.array(
  z.object({
    fulfillmentOrderId: z.string(),
    fulfillmentOrderLineItems: z.array(OutboundShipmentLineItemSchema),
  }),
);
export type LineItemsByFulfillmentOrder = z.infer<
  typeof LineItemsByFulfillmentOrderSchema
>;

// https://support.easypost.com/hc/en-us/articles/360044353091-Tracking-Frequently-Asked-Questions#h_9656edcc-0b76-4851-8b09-a826d9add238
export enum ShipmentTrackerStatus {
  UNKNOWN = "unknown",
  PRE_TRANSIT = "pre_transit",
  IN_TRANSIT = "in_transit",
  OUT_FOR_DELIVERY = "out_for_delivery",
  AVAILABLE_FOR_PICKUP = "available_for_pickup",
  DELIVERED = "delivered",
  RETURN_TO_SENDER = "return_to_sender",
  FAILURE = "failure",
  CANCELLED = "cancelled",
  ERROR = "error",
  // Our own status for tracking shipments that are stuck in transit
  STALLED_IN_TRANSIT = "stalled_in_transit",
}

// TODO - we might need to revisit this - is "Cancelled" a terminal status?
export const TerminalOutboundTrackingStatuses = [
  ShipmentTrackerStatus.DELIVERED,
  ShipmentTrackerStatus.RETURN_TO_SENDER,
  ShipmentTrackerStatus.FAILURE,
  ShipmentTrackerStatus.CANCELLED,
  ShipmentTrackerStatus.ERROR,
];

// We rely on easypost webhooks for tracking syncs right now
export const TrackingPlatformsWeManuallySync = [
  TrackingPlatform.UPS,
  TrackingPlatform.FIRST_MILE,
  TrackingPlatform.EPOST_GLOBAL,
  TrackingPlatform.APC,
  TrackingPlatform.LANDMARK_GLOBAL,
  TrackingPlatform.GLOBALE,
  TrackingPlatform.WWEX,
  TrackingPlatform.DHL_ECS,
  TrackingPlatform.USPS,
  TrackingPlatform.UNIUNI,
  TrackingPlatform.STALLION_EXPRESS,
  TrackingPlatform.ECOM_EXPRESS,
  TrackingPlatform.FREIGHTWIRE,
  TrackingPlatform.AMAZON_BUY_SHIPPING,
  TrackingPlatform.CHITCHATS,
];

export const TrackingDetailSchema = z.object({
  carrier_code: z.string().nullish(),
  datetime: z.date(),
  description: z.string().nullish(),
  message: z.string(),
  object: z.string().nullish(),
  source: z.string().nullish(),
  status: createEnum(ShipmentTrackerStatus, {
    enumName: "ShipmentTrackerStatus",
  }),
  status_detail: z.string().nullish(),
  tracking_location: z
    .object({
      city: z.string().nullish(),
      country: z.string().nullish(),
      object: z.string().nullish(),
      state: z.string().nullish(),
      zip: z.string().nullish(),
    })
    .nullish(),
});

export type ShipmentTrackerTrackingDetail = z.infer<
  typeof TrackingDetailSchema
>;

export const ShipmentTrackerSchema = z.object({
  id: z.string(),
  trackingPlatform: createEnum(TrackingPlatform, {
    enumName: "TrackingPlatform",
  }).default(TrackingPlatform.EASYPOST),
  status: createEnum(ShipmentTrackerStatus, {
    enumName: "ShipmentTrackerStatus",
  }),
  trackingCode: z.string(),
  carrier: z.string(),
  publicUrl: z.string(),
  estDeliveryDate: z.date().nullish(),
  trackingDetails: z.array(TrackingDetailSchema).nullish(),
  raw: z.any().nullish(),
});

export type OutboundShipmentTracker = z.infer<typeof ShipmentTrackerSchema>;

export const CanadaPostManifestSchema = z.object({
  manifestMode: createEnum(CanadaPostManifestMode, {
    enumName: "CanadaPostManifestMode",
  }),
  manifestRequired: z.boolean(),
  groupId: z.string().nullish(),
  shipmentId: z.string().nullish(),
  trackingPin: z.string().nullish(),
  manifestId: z.string().nullish(),
  manifestedAt: z.date().nullish(),
});
export type CanadaPostManifest = z.infer<typeof CanadaPostManifestSchema>;

export const ShipmentPackageSchema_ = z.object({
  label: OutboundShipmentLabelSchema.nullish(),
  boughtRate: RateSchema.nullish(),
  parcel: OutboundParcelSchema.nullish(),
  lineItemsByFulfillmentOrder: LineItemsByFulfillmentOrderSchema,
  slip: z.object({ printed: z.date().nullish() }).nullish(),
  labelSlipUrl: z.string().nullish(),
  scanFormId: z.string().nullish(),
  scanFormStatus: createEnum(ScanFormStatus, {
    enumName: "ScanFormStatus",
  }).nullish(),
  scanFormBatch: z
    .object({
      id: z.string(),
      state: createEnum(BatchState, { enumName: "BatchState" }),
    })
    .nullish(),
  voided: z.boolean().nullish(),
  tracker: ShipmentTrackerSchema.nullish(),
  insured: z.boolean().nullish(),
  insuredValue: z.number().nullish(),
  merchantCarrierAccountLabelFee: z.number().nullish(),
  canadaPostManifest: CanadaPostManifestSchema.nullish(),
  recordedShipment: RecordedShipmentSchema.nullish(),
  createdAt: z.date().nullish(),
  updatedAt: z.date().nullish(),
  shippingClaimIds: z.array(z.string()).nullish(),
  shipsuranceClaims: z
    .array(ShipsuranceClaimSchema as IShipsuranceClaim)
    .nullish(),
  surcharges: z
    .array(surchargeSchema_.and(z.object({ balanceTransactionId: z.string() })))
    .nullish(),
});
export const ShipmentPackageSchema: z.ZodType<ShipmentPackage> =
  ShipmentPackageSchema_;
export interface ShipmentPackage extends z.infer<
  typeof ShipmentPackageSchema_
> {}
export interface IShipmentPackage extends z.ZodType<ShipmentPackage> {}

// A shipment with no label and no bought rate was never actually purchased —
// failed buys can leave one behind, and treating it as bought strands the group.
export function isPurchasedShipment(
  shipment: ShipmentPackage | null | undefined,
): boolean {
  return !!(shipment?.label?.url || shipment?.boughtRate);
}

const FreightShipmentSchema_ = z.object({
  freightBookingId: freightBookingIdFormat.schema,
  billOfLadingUrl: z.string(),
  scac: createEnum(KnownSCAC, { enumName: "KnownSCAC" }),
  serviceLevel: createEnum(FreightServiceLevel, {
    enumName: "FreightServiceLevel",
  }),
  quoteId: z.string(),
  shipmentTransactionId: z.string(),
  providerCost: z.string(),
  cost: z.string(),
  currency: createEnum(Currency, { enumName: "Currency" }),
  pickupDate: z.date(),
  estDeliveryDays: z.number().nullish(),
  bolNumber: z.string(),
  trackingLink: z.string(),
  pickupTransactionId: z.string(),
  tracker: ShipmentTrackerSchema,
  provider: createEnum(FreightProvider, { enumName: "FreightProvider" }),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const FreightShipmentSchema: z.ZodType<FreightShipment> =
  FreightShipmentSchema_;
export interface FreightShipment extends z.infer<
  typeof FreightShipmentSchema_
> {}

export const ShipmentDetailsSchema = z.object({
  createdAt: z.date(),
  updatedAt: z.date(),
  voided: z.boolean(),
  shipments: z.array(ShipmentPackageSchema),
  freightShipment: FreightShipmentSchema.nullish(),
});

export function getSuccessFulfillmentsByTrackingCode(
  fulfillmentGroup: FulfillmentGroup,
): RedoFulfillment[] {
  const trackingCode =
    fulfillmentGroup.shipmentDetails?.freightShipment?.bolNumber ??
    fulfillmentGroup.shipment?.tracker?.trackingCode;
  if (!trackingCode) {
    throw new Error("No tracking code on shipment");
  }
  const fulfillments =
    fulfillmentGroup.fulfillments?.filter(
      (f) =>
        f.trackingInfo?.find((t) => t.number === trackingCode) &&
        f.status === RedoShopifyFulfillmentStatus.Success,
    ) ?? [];

  return fulfillments;
}

// https://shopify.dev/docs/api/admin-graphql/latest/objects/FulfillmentTrackingInfo#supported-tracking-companies
export function getShopifyFriendlyCarrierName(
  carrier: Carriers | undefined,
): string {
  if (!carrier) {
    return "";
  }
  switch (carrier) {
    case Carriers.FEDEX:
    case Carriers.FEDEX_SMART_POST:
      return "FedEx";
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
      return "UPS";
    case Carriers.USPS:
    case Carriers.USPS_SHIP:
    case Carriers.USPS_RETURNS:
      return "USPS";
    case Carriers.AMAZON:
      return "Amazon";
    case Carriers.DHL_EXPRESS:
      return "DHL Express";
    case Carriers.VEHO:
      return "Veho";
    case Carriers.FIRST_MILE:
      return "FirstMile";
    case Carriers.DHL_ECS:
      return "DHL eCommerce";
    case Carriers.APC:
      return "APC";
    case Carriers.AUSTRALIA_POST:
      return "Australia Post";
    case Carriers.CANADA_POST:
      return "Canada Post";
    case Carriers.GLS:
      return "GLS";
    case Carriers.ASENDIA:
      return "Asendia";
    case Carriers.ROYAL_MAIL_V3:
      return "Royal Mail V3";
    case Carriers.SPEE_DEE:
      return "SpeeDee";
    case Carriers.PASSPORT_GLOBAL:
      return "Passport Global";
    case Carriers.LANDMARK_GLOBAL:
      return "Landmark Global";
    case Carriers.ETA_EXPRESS:
      return "ETA Express";
    case Carriers.EPOST_GLOBAL:
      return "ePost Global";
    case Carriers.ONTRAC:
      return "OnTrac";
    case Carriers.GLOBALE:
      return "Global-E";
    case Carriers.UPS_MAIL_INNOVATIONS:
      return "UPS";
    case Carriers.UNIUNI:
      return "uniuni";
    case Carriers.PUROLATOR:
      return "Purolator";
    case Carriers.STALLION_EXPRESS:
      return "Stallion Express";
    case Carriers.ECOM_EXPRESS:
      return "Ecom Express";
    case Carriers.CHITCHATS:
      return "Chit Chats";
    case Carriers.POST_NL:
      return "PostNL";
    case Carriers.INTELCOM:
      return "Intelcom";
    case Carriers.FLEET_OPTICS:
      return "Fleet Optics";
    case Carriers.ICS_COURIER:
      return "ICS Courier";
    case Carriers.WHIZ_DELIVERY:
      return "Whiz Delivery";
    case Carriers.TIKTOK_SHOP:
      return "Tik Tok";
    case Carriers.CANPAR_EXPRESS:
      return "Canpar";
    default:
      assertNever(carrier);
  }
}

// https://developer.bigcommerce.com/docs/rest-management/orders/order-shipments#create-order-shipment
export function getBigcommerceFriendlyCarrierName(
  carrier: Carriers | undefined,
): string {
  if (!carrier) {
    return "";
  }
  switch (carrier) {
    case Carriers.FEDEX:
    case Carriers.FEDEX_SMART_POST:
      return "fedex";
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
      return "ups";
    case Carriers.USPS:
    case Carriers.USPS_SHIP:
    case Carriers.USPS_RETURNS:
      return "usps";
    case Carriers.AMAZON:
      return "";
    case Carriers.DHL_EXPRESS:
      return "";
    case Carriers.VEHO:
      return "";
    case Carriers.FIRST_MILE:
      return "";
    case Carriers.DHL_ECS:
      return "";
    case Carriers.APC:
      return "";
    case Carriers.AUSTRALIA_POST:
      return "";
    case Carriers.CANADA_POST:
      return "";
    case Carriers.GLS:
      return "";
    case Carriers.ASENDIA:
      return "";
    case Carriers.ROYAL_MAIL_V3:
      return "";
    case Carriers.SPEE_DEE:
      return "";
    case Carriers.PASSPORT_GLOBAL:
      return "";
    case Carriers.LANDMARK_GLOBAL:
      return "";
    case Carriers.ETA_EXPRESS:
      return "ETA Express";
    case Carriers.EPOST_GLOBAL:
      return "";
    case Carriers.ONTRAC:
      return "";
    case Carriers.GLOBALE:
      return "";
    case Carriers.UPS_MAIL_INNOVATIONS:
      return "ups";
    case Carriers.UNIUNI:
      return "";
    case Carriers.PUROLATOR:
      return "";
    case Carriers.STALLION_EXPRESS:
    case Carriers.ECOM_EXPRESS:
    case Carriers.CHITCHATS:
    case Carriers.POST_NL:
    case Carriers.INTELCOM:
    case Carriers.FLEET_OPTICS:
    case Carriers.ICS_COURIER:
    case Carriers.WHIZ_DELIVERY:
    case Carriers.TIKTOK_SHOP:
      return "";
    case Carriers.CANPAR_EXPRESS:
      return "";
    default:
      assertNever(carrier);
  }
}

// TikTok returns carrier/service names with trailing dots ("UPS.", "FedEx..")
// and trademark symbols ("UPS®", "FedEx Ground® Economy") that break exact and
// substring matching against our names.
export function normalizeTiktokShippingName(name: string): string {
  return name.replace(/[®™]/g, "").replace(/\.+$/, "").trim();
}

// https://partner.tiktokshop.com/docv2/page/650aa48d4a0bb702c06d85cd?external_id=650aa48d4a0bb702c06d85cd
export function getTiktokFriendlyCarrierName(
  carrier: Carriers | undefined,
): string {
  if (!carrier) {
    return "";
  }

  switch (carrier) {
    case Carriers.FEDEX:
    case Carriers.FEDEX_SMART_POST:
      return "FedEx";
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
      return "UPS";
    case Carriers.USPS:
    case Carriers.USPS_SHIP:
    case Carriers.USPS_RETURNS:
      return "USPS";
    case Carriers.AMAZON:
      return "Amazon shipping + Amazon MCF";
    case Carriers.DHL_EXPRESS:
      return "DHL express";
    case Carriers.VEHO:
      return "Veho";
    case Carriers.FIRST_MILE:
      return "First Mile";
    case Carriers.DHL_ECS:
      return "DHL eCommerce";
    case Carriers.APC:
      return "";
    case Carriers.AUSTRALIA_POST:
      return "";
    case Carriers.CANADA_POST:
      return "";
    case Carriers.GLS:
      return "GLS US";
    case Carriers.ASENDIA:
      return "Asendia US";
    case Carriers.ROYAL_MAIL_V3:
      return "";
    case Carriers.SPEE_DEE:
      return "Spee-Dee Delivery";
    case Carriers.PASSPORT_GLOBAL:
      return "";
    case Carriers.LANDMARK_GLOBAL:
      return "";
    case Carriers.ETA_EXPRESS:
      return "";
    case Carriers.EPOST_GLOBAL:
      return "";
    case Carriers.ONTRAC:
      return "OnTrac";
    case Carriers.GLOBALE:
      return "";
    case Carriers.UPS_MAIL_INNOVATIONS:
      return "UPS";
    case Carriers.UNIUNI:
      return "UniUni";
    case Carriers.PUROLATOR:
      return "";
    case Carriers.STALLION_EXPRESS:
      return "Stallion Express";
    case Carriers.ECOM_EXPRESS:
      return "";
    case Carriers.CHITCHATS:
      return "Chit Chats";
    case Carriers.POST_NL:
      return "PostNL";
    case Carriers.INTELCOM:
      return "Intelcom";
    case Carriers.FLEET_OPTICS:
      return "Fleet Optics";
    case Carriers.ICS_COURIER:
      return "ICS Courier";
    case Carriers.WHIZ_DELIVERY:
      return "Whiz Delivery";
    case Carriers.TIKTOK_SHOP:
      return "TikTok";
    case Carriers.CANPAR_EXPRESS:
      return "";
    default:
      assertNever(carrier);
  }
}

// https://developer.ebay.com/devzone/XML/docs/Reference/eBay/types/ShippingCarrierCodeType.html
export function getEbayFriendlyCarrierName(
  carrier: Carriers | undefined,
): string {
  if (!carrier) {
    return "";
  }

  switch (carrier) {
    case Carriers.FEDEX:
      return "FedEx";
    case Carriers.FEDEX_SMART_POST:
      return "FedExSmartPost";
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
      return "UPS";
    case Carriers.USPS:
    case Carriers.USPS_SHIP:
    case Carriers.USPS_RETURNS:
      return "USPS";
    case Carriers.AMAZON:
      return "";
    case Carriers.DHL_EXPRESS:
      return "DHL";
    case Carriers.VEHO:
      return "";
    case Carriers.FIRST_MILE:
      return "";
    case Carriers.DHL_ECS:
      return "DHLGlobalMail"; // Global mail was rebranded to dhl ecommerce in 2014
    case Carriers.APC:
      return "APC";
    case Carriers.AUSTRALIA_POST:
      return "AustraliaPost";
    case Carriers.CANADA_POST:
      return "CanadaPost";
    case Carriers.GLS:
      return "GLS";
    case Carriers.ASENDIA:
      return "";
    case Carriers.ROYAL_MAIL_V3:
      return "Royal Mail V3";
    case Carriers.SPEE_DEE:
      return "SpeeDee";
    case Carriers.PASSPORT_GLOBAL:
      return "Passport Global";
    case Carriers.LANDMARK_GLOBAL:
      return "Landmark";
    case Carriers.ETA_EXPRESS:
      return "ETA Express";
    case Carriers.EPOST_GLOBAL:
      return "ePost Global";
    case Carriers.ONTRAC:
      return "OnTrac";
    case Carriers.GLOBALE:
      return "";
    case Carriers.UPS_MAIL_INNOVATIONS:
      return "UPS Mail Innovations";
    case Carriers.UNIUNI:
      return "UniUni";
    case Carriers.PUROLATOR:
      return "";
    case Carriers.STALLION_EXPRESS:
    case Carriers.ECOM_EXPRESS:
    case Carriers.CHITCHATS:
    case Carriers.POST_NL:
    case Carriers.INTELCOM:
    case Carriers.FLEET_OPTICS:
    case Carriers.ICS_COURIER:
    case Carriers.WHIZ_DELIVERY:
    case Carriers.TIKTOK_SHOP:
      return "";
    case Carriers.CANPAR_EXPRESS:
      return "";
    default:
      assertNever(carrier);
  }
}

// https://developer.walmart.com/api/us/mp/orders#operation/shipOrderLines
export function getWalmartFriendlyCarrierName(
  carrier: Carriers | undefined,
): string | undefined {
  if (!carrier) {
    return undefined;
  }

  switch (carrier) {
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
      return "UPS";
    case Carriers.USPS:
    case Carriers.USPS_RETURNS:
    case Carriers.USPS_SHIP:
      return "USPS";
    case Carriers.FEDEX:
      return "FedEx";
    case Carriers.FEDEX_SMART_POST:
      return "FEDEXSP";
    case Carriers.DHL_EXPRESS:
    case Carriers.DHL_ECS:
      return "DHL Ecommerce - US";
    case Carriers.FIRST_MILE:
      return "FIRST MILE";
    case Carriers.GLS:
      return "GLS";
    case Carriers.LANDMARK_GLOBAL:
      return "Landmark Global";
    case Carriers.AMAZON:
    case Carriers.VEHO:
    case Carriers.AUSTRALIA_POST:
    case Carriers.CANADA_POST:
    case Carriers.APC:
    case Carriers.ASENDIA:
    case Carriers.ROYAL_MAIL_V3:
    case Carriers.SPEE_DEE:
    case Carriers.PASSPORT_GLOBAL:
    case Carriers.ETA_EXPRESS:
      return undefined;
    case Carriers.EPOST_GLOBAL:
      return undefined;
    case Carriers.ONTRAC:
      return "OnTrac";
    case Carriers.GLOBALE:
      return undefined;
    case Carriers.UPS_MAIL_INNOVATIONS:
      return "UPSMI";
    case Carriers.UNIUNI:
      return "UniUni";
    case Carriers.PUROLATOR:
      return undefined;
    case Carriers.STALLION_EXPRESS:
    case Carriers.ECOM_EXPRESS:
    case Carriers.CHITCHATS:
    case Carriers.POST_NL:
    case Carriers.INTELCOM:
    case Carriers.FLEET_OPTICS:
    case Carriers.ICS_COURIER:
    case Carriers.WHIZ_DELIVERY:
    case Carriers.TIKTOK_SHOP:
      return undefined;
    case Carriers.CANPAR_EXPRESS:
      return undefined;
    default:
      assertNever(carrier);
  }
}

// https://www.wayfair.com/supplier/home/documentation/dropship-supplier-api-docs
// Returns the SCAC carrier code expected by the Wayfair shipment API.
export function getWayfairScacCarrierCode(
  carrier: Carriers | undefined,
): string | undefined {
  if (!carrier) {
    return undefined;
  }

  switch (carrier) {
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
    case Carriers.UPS_MAIL_INNOVATIONS:
      return "UPSN";
    case Carriers.USPS:
    case Carriers.USPS_SHIP:
    case Carriers.USPS_RETURNS:
      return "USPS";
    case Carriers.FEDEX:
      return "FDEG";
    case Carriers.FEDEX_SMART_POST:
      return "FDXE";
    case Carriers.DHL_EXPRESS:
    case Carriers.DHL_ECS:
      return "DHLW";
    case Carriers.AMAZON:
    case Carriers.VEHO:
    case Carriers.FIRST_MILE:
    case Carriers.APC:
    case Carriers.AUSTRALIA_POST:
    case Carriers.CANADA_POST:
    case Carriers.GLS:
    case Carriers.ASENDIA:
    case Carriers.ROYAL_MAIL_V3:
    case Carriers.SPEE_DEE:
    case Carriers.PASSPORT_GLOBAL:
    case Carriers.LANDMARK_GLOBAL:
    case Carriers.ETA_EXPRESS:
    case Carriers.EPOST_GLOBAL:
    case Carriers.ONTRAC:
    case Carriers.GLOBALE:
    case Carriers.PUROLATOR:
    case Carriers.UNIUNI:
    case Carriers.STALLION_EXPRESS:
    case Carriers.ECOM_EXPRESS:
    case Carriers.CHITCHATS:
    case Carriers.POST_NL:
    case Carriers.INTELCOM:
    case Carriers.FLEET_OPTICS:
    case Carriers.ICS_COURIER:
    case Carriers.WHIZ_DELIVERY:
    case Carriers.TIKTOK_SHOP:
      return undefined;
    case Carriers.CANPAR_EXPRESS:
      return undefined;
    default:
      assertNever(carrier);
  }
}

// CS carriers must be at most 10 characters
export function getCommentsoldFriendlyCarrierName(
  carrier: Carriers | undefined,
): string {
  if (!carrier) {
    return "";
  }

  switch (carrier) {
    case Carriers.FEDEX:
    case Carriers.FEDEX_SMART_POST:
      return "FedEx";
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
      return "UPS";
    case Carriers.USPS:
    case Carriers.USPS_SHIP:
    case Carriers.USPS_RETURNS:
      return "USPS";
    case Carriers.AMAZON:
      return "Amazon";
    case Carriers.DHL_EXPRESS:
    case Carriers.DHL_ECS:
      return "DHL";
    case Carriers.VEHO:
      return "Veho";
    case Carriers.FIRST_MILE:
      return "FirstMile";
    case Carriers.APC:
      return "APC";
    case Carriers.AUSTRALIA_POST:
      return "Aus Post";
    case Carriers.CANADA_POST:
      return "CanadaPost";
    case Carriers.GLS:
      return "GLS";
    case Carriers.ASENDIA:
      return "Asendia";
    case Carriers.ROYAL_MAIL_V3:
      return "Royal Mail";
    case Carriers.SPEE_DEE:
      return "SpeeDee";
    case Carriers.PASSPORT_GLOBAL:
      return "Passport";
    case Carriers.LANDMARK_GLOBAL:
      return "Landmark";
    case Carriers.ETA_EXPRESS:
      return "ETA";
    case Carriers.EPOST_GLOBAL:
      return "ePost";
    case Carriers.ONTRAC:
      return "OnTrac";
    case Carriers.GLOBALE:
      return "Global-E";
    case Carriers.UPS_MAIL_INNOVATIONS:
      return "UPS MI";
    case Carriers.UNIUNI:
      return "UniUni";
    case Carriers.PUROLATOR:
      return "Purolator";
    case Carriers.STALLION_EXPRESS:
      return "Stallion";
    case Carriers.ECOM_EXPRESS:
      return "Ecom";
    case Carriers.CHITCHATS:
      return "Chit Chats";
    case Carriers.POST_NL:
      return "PostNL";
    case Carriers.INTELCOM:
      return "Intelcom";
    case Carriers.FLEET_OPTICS:
      return "FleetOptics";
    case Carriers.ICS_COURIER:
      return "ICS";
    case Carriers.WHIZ_DELIVERY:
      return "WhizDelivery";
    case Carriers.TIKTOK_SHOP:
      return "Tik Tok";
    case Carriers.CANPAR_EXPRESS:
      return "Canpar";
    default:
      assertNever(carrier);
  }
}

// https://developers.faire.com/docs#/schemas/ExternalShipmentV2
export enum FaireCarrier {
  CANADA_POST = "CANADA_POST",
  DHL_ECOMMERCE = "DHL_ECOMMERCE",
  DHL_EXPRESS = "DHL_EXPRESS",
  FEDEX = "FEDEX",
  PUROLATOR = "PUROLATOR",
  UPS = "UPS",
  USPS = "USPS",
  POSTNL = "POSTNL",
  CANPAR = "CANPAR",
  INTERLINK_EXPRESS = "INTERLINK_EXPRESS",
  GSO = "GSO",
  ROYAL_MAIL = "ROYAL_MAIL",
  DPD = "DPD",
  DPDUK = "DPDUK",
  PARCELFORCE = "PARCELFORCE",
  AUSTRALIA_POST = "AUSTRALIA_POST",
  EVRI = "EVRI",
  LA_POSTE = "LA_POSTE",
}

export function mapRedoCarrierToFaire(redoCarrier: Carriers): string {
  switch (redoCarrier) {
    case Carriers.CANADA_POST:
      return FaireCarrier.CANADA_POST;
    case Carriers.DHL_ECS:
      return FaireCarrier.DHL_ECOMMERCE;
    case Carriers.DHL_EXPRESS:
      return FaireCarrier.DHL_EXPRESS;
    case Carriers.FEDEX:
    case Carriers.FEDEX_SMART_POST:
      return FaireCarrier.FEDEX;
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
      return FaireCarrier.UPS;
    case Carriers.USPS:
    case Carriers.USPS_SHIP:
    case Carriers.USPS_RETURNS:
      return FaireCarrier.USPS;
    case Carriers.ROYAL_MAIL_V3:
      return FaireCarrier.ROYAL_MAIL;
    case Carriers.AUSTRALIA_POST:
      return FaireCarrier.AUSTRALIA_POST;
  }

  return redoCarrier;
}
